import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import {
  Download,
  FileSpreadsheet,
  FileCode,
  FileText,
  FileImage,
  File as FileIcon,
  Loader2,
  Maximize2,
  X,
} from 'lucide-react'
import { documentKindFromName, documentKindLabel, type DocumentKind } from '@/utils/documentKind'
import { useDocumentBlob } from './useDocumentBlob'
import { PdfInline, PdfFull } from './PdfViewer'
import { SpreadsheetViewer } from './SpreadsheetViewer'
import { CsvViewer } from './CsvViewer'
import { XmlViewer } from './XmlViewer'

function KindIcon({ kind, className }: { kind: DocumentKind; className: string }) {
  switch (kind) {
    case 'spreadsheet':
    case 'csv':
      return <FileSpreadsheet className={className} />
    case 'xml':
      return <FileCode className={className} />
    case 'image':
      return <FileImage className={className} />
    case 'pdf':
    case 'text':
      return <FileText className={className} />
    default:
      return <FileIcon className={className} />
  }
}

/**
 * What a format with no viewer of its own looks like: the kind of file it is
 * and a way to get it. A .xls workbook or a drawing lands here, which is the
 * honest outcome — the preview cannot show it, and saying so beats an error.
 */
function FileCard({ name, kind, objectUrl }: { name: string; kind: DocumentKind; objectUrl: string | null }) {
  return (
    <div className="flex h-full min-h-48 flex-col items-center justify-center gap-3 px-6 py-8 text-center">
      <KindIcon kind={kind} className="h-10 w-10 text-gray-300" />
      <div>
        <p className="break-all text-sm text-gray-700">{name}</p>
        <p className="mt-0.5 text-xs text-gray-400">{documentKindLabel(name)} file</p>
      </div>
      {objectUrl && (
        <a
          href={objectUrl}
          download={name}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
        >
          <Download className="h-3.5 w-3.5" />
          Download
        </a>
      )}
    </div>
  )
}

function Body({
  kind,
  name,
  blob,
  objectUrl,
  full,
}: {
  kind: DocumentKind
  name: string
  blob: Blob | null
  objectUrl: string | null
  full: boolean
}) {
  if (!objectUrl) return <FileCard name={name} kind={kind} objectUrl={null} />

  switch (kind) {
    case 'pdf':
      return full ? <PdfFull fileUrl={objectUrl} /> : <PdfInline fileUrl={objectUrl} />

    case 'spreadsheet':
      return <SpreadsheetViewer blob={blob} fileName={name} />

    case 'csv':
      return <CsvViewer blob={blob} full={full} />

    case 'xml':
      return <XmlViewer blob={blob} full={full} />

    case 'image':
      return (
        <div className={full ? 'min-h-0 flex-1 overflow-auto bg-gray-100 p-4' : 'overflow-hidden'}>
          <img src={objectUrl} alt={name} className={full ? 'mx-auto max-w-none' : 'h-full w-full object-contain'} />
        </div>
      )

    default:
      return <FileCard name={name} kind={kind} objectUrl={objectUrl} />
  }
}

/**
 * One document, rendered according to what it is. Everything shares the same
 * frame: a boxed preview with download and full-screen buttons, and a dialog
 * that gives the format room to be read properly.
 */
export function DocumentViewer({ attachmentId, name }: { attachmentId: string; name: string }) {
  const { objectUrl, blob, loading, error } = useDocumentBlob(attachmentId)
  const [dialogOpen, setDialogOpen] = useState(false)

  const kind = documentKindFromName(name)

  if (loading) {
    return (
      <div className="flex min-h-48 w-full items-center justify-center rounded-sheet border border-sheet-border bg-gray-50">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error || !objectUrl) {
    return (
      <div className="flex min-h-48 w-full items-center justify-center rounded-sheet border border-sheet-border bg-gray-50 px-4 text-center text-xs text-gray-400">
        {error ?? 'Could not load the document.'}
      </div>
    )
  }

  const overlayButton = 'rounded-md bg-white/80 p-1.5 text-gray-500 shadow-sm backdrop-blur-sm hover:bg-white'

  // The spreadsheet viewer comes with its own toolbar along the top, carrying a
  // download of its own. Ours would sit on top of it and offer the same thing
  // twice, so where the body has its own chrome we contribute only the control
  // it does not have, out of the toolbar's way.
  const bodyHasOwnChrome = kind === 'spreadsheet'

  return (
    <>
      <div
        className={
          // The workbook viewer lays itself out in flex and lets its sheet tabs
          // shrink, so a box that only caps its height crushes the tab strip
          // against the grid. It gets a definite height instead.
          bodyHasOwnChrome
            ? 'relative flex h-[min(70vh,560px)] w-full max-w-full flex-col overflow-hidden rounded-sheet border border-sheet-border bg-gray-50'
            : 'relative flex max-h-[min(50vh,480px)] min-h-48 w-full max-w-full flex-col overflow-hidden rounded-sheet border border-sheet-border bg-gray-50'
        }
      >
        <Body kind={kind} name={name} blob={blob} objectUrl={objectUrl} full={false} />

        {/* Download saves the copy already fetched for the preview (a local blob),
            under the attachment's own name: no extra request. */}
        <div className={`absolute z-20 flex gap-1 ${bodyHasOwnChrome ? 'bottom-2 right-3' : 'right-2 top-2'}`}>
          {!bodyHasOwnChrome && (
            <a href={objectUrl} download={name} className={overlayButton} title="Download" aria-label={`Download ${name}`}>
              <Download className="h-4 w-4" />
            </a>
          )}
          <button type="button" onClick={() => setDialogOpen(true)} className={overlayButton} title="Open full screen">
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
          <Dialog.Content
            className="fixed inset-4 z-50 flex flex-col overflow-hidden rounded-panel bg-white shadow-2xl sm:inset-8"
            aria-describedby={undefined}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
              <Dialog.Title className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <KindIcon kind={kind} className="h-4 w-4 text-gray-400" />
                {name}
              </Dialog.Title>
              <div className="flex items-center gap-1">
                {!bodyHasOwnChrome && (
                  <a
                    href={objectUrl}
                    download={name}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Download"
                    aria-label={`Download ${name}`}
                  >
                    <Download className="h-5 w-5" />
                  </a>
                )}
                <Dialog.Close className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </Dialog.Close>
              </div>
            </div>

            {dialogOpen && <Body kind={kind} name={name} blob={blob} objectUrl={objectUrl} full />}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
